<template>
  <hs-modal
    :model-value="true"
    title="Grant Jack Store Access"
    description="Create a time-boxed support access grant with scoped permissions and audit visibility."
    size="xl"
    @close="handleClose"
  >
    <div class="jack-access-modal">
      <div class="space-y-6">
        <section
          class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-slate-800"
        >
          <header class="mb-4 flex items-center justify-between">
            <div>
              <h2 class="text-base font-semibold text-gray-900 dark:text-white">
                Access Context
              </h2>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Choose the store and teammate who will receive the temporary
                access.
              </p>
            </div>
            <hs-badge tone="primary" icon="key">
              Jack
            </hs-badge>
          </header>

          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <label
                class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Store</label
              >
              <async-store-select
                :model-value="selectedStoreSummary"
                :include-all-option="false"
                placeholder="Search stores"
                @update:modelValue="handleStoreSelected"
              />
              <p v-if="errors.storeId" class="mt-1 text-xs text-red-500">
                {{ errors.storeId }}
              </p>
            </div>

            <div v-if="storeMembersOptions.length">
              <label
                class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >User Selection Mode</label
              >
              <hs-tabs
                :model-value="form.userMode"
                :tabs="userModeTabs"
                @update:modelValue="setUserMode"
              />
            </div>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <div
              v-if="form.userMode === 'existing' && storeMembersOptions.length"
            >
              <label
                class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Store teammate
              </label>
              <div
                v-if="storeMembersLoading"
                class="h-11 animate-pulse rounded-lg bg-gray-200 dark:bg-slate-700"
              ></div>
              <div v-else>
                <hs-select
                  v-model="selectedMemberModel"
                  :options="storeMembersOptions"
                  label="Store teammate"
                  placeholder="Select teammate"
                  :error="errors.targetUser"
                />
                <p
                  v-if="!storeMembersOptions.length"
                  class="mt-1 text-xs text-amber-600"
                >
                  No store members found. Switch to manual ID to grant access to
                  an external teammate.
                </p>
              </div>
            </div>

            <div
              v-if="form.userMode === 'manual' || !storeMembersOptions.length"
            >
              <hs-input
                v-model="form.manualPhoneNumber"
                label="User who gets access"
                type="tel"
                icon="phone"
                placeholder="eg: +250XXXXXXXXXX"
                :error="errors.targetUser"
              />
              <div class="mt-1 text-xs">
                <span
                  v-if="phoneLookupLoading"
                  class="text-blue-600 dark:text-blue-300"
                  >Validating phone number…</span
                >
                <span
                  v-else-if="phoneLookupResult"
                  class="text-emerald-600 dark:text-emerald-300"
                >
                  {{
                    phoneLookupResult.names ||
                    phoneLookupResult.username ||
                    phoneLookupResult.phone_number
                  }}
                </span>
                <span v-else-if="phoneLookupError" class="text-red-500">{{
                  phoneLookupError
                }}</span>
                <span v-else class="text-gray-500 dark:text-gray-400">
                  The phone number must belong to an existing Hova user account.
                </span>
              </div>
            </div>

            <div class="md:col-span-2">
              <hs-textarea
                v-model="form.reason"
                label="Purpose"
                :rows="2"
                maxlength="240"
                placeholder="Explain why this access is needed (visible in audit logs)."
                :error="errors.reason"
                required
              />
              <div
                class="mt-1 flex justify-between text-xs text-gray-500 dark:text-gray-400"
              >
                <span></span>
                <span>{{ reasonCharacterCount }}/240</span>
              </div>
            </div>
          </div>
        </section>

        <section
          class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-slate-800"
        >
          <header class="mb-4">
            <h2 class="text-base font-semibold text-gray-900 dark:text-white">
              Permissions
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Blend presets with granular controls to tailor the access scope.
            </p>
          </header>

          <div class="mb-4 flex flex-wrap gap-2">
            <button
              v-for="preset in filteredPermissionPresets"
              :key="preset.key"
              type="button"
              :class="presetButtonClass(preset)"
              @click="togglePreset(preset)"
            >
              <span
                class="block text-sm font-semibold"
                :class="presetLabelClass(preset)"
                >{{ preset.label }}</span
              >
              <span
                class="mt-1 block text-[11px] font-normal normal-case"
                :class="presetDescriptionClass(preset)"
              >
                {{ preset.description }}
              </span>
            </button>
          </div>

          <div class="flex flex-wrap items-center justify-between gap-3">
            <div
              class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
            >
              <hs-badge tone="neutral" size="sm">
                {{ form.permissionSelection.length }} selected
              </hs-badge>
              <hs-button variant="ghost" size="sm" @click="clearPermissions">
                Clear all
              </hs-button>
            </div>
            <div class="relative">
              <hs-input
                v-model="form.permissionSearch"
                class="w-56"
                type="search"
                icon="search"
                placeholder="Search permissions"
              />
            </div>
          </div>

          <div class="mt-5 space-y-4">
            <div
              v-for="group in filteredPermissionGroups"
              :key="group.key"
              class="rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-inner transition hover:border-blue-200 dark:border-gray-700 dark:bg-slate-900"
            >
              <details open>
                <summary
                  class="flex cursor-pointer items-center justify-between gap-3"
                >
                  <span
                    class="text-sm font-semibold text-gray-800 dark:text-white"
                    >{{ group.label }}</span
                  >
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    {{ group.options.length }} options ·
                    {{ group.readOnlyCount }} read ·
                    {{ group.writeCount }} elevated
                  </span>
                </summary>
                <div class="mt-3 grid gap-2 md:grid-cols-2">
                  <div
                    v-for="option in group.options"
                    :key="option.value"
                    class="jack-access-modal__permission"
                  >
                    <hs-checkbox
                      :model-value="isPermissionSelected(option.value)"
                      :label="option.label"
                      :description="generatePermissionDescription(option.value)"
                      @update:modelValue="setPermissionSelected(option.value, $event)"
                    />
                    <hs-badge :tone="option.readOnly ? 'success' : 'warning'" size="sm">
                      {{ option.readOnly ? "Read-only" : "Elevated" }}
                    </hs-badge>
                  </div>
                </div>
              </details>
            </div>
            <p v-if="errors.permissions" class="text-xs text-red-500">
              {{ errors.permissions }}
            </p>
            <p
              v-else-if="!isSuperAdmin && hasElevatedPermissions"
              class="text-xs text-amber-600"
            >
              Elevated permissions (write/manage) require super admin
              privileges. The API will reject this grant otherwise.
            </p>
          </div>
        </section>

        <section
          class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-slate-800"
        >
          <header class="mb-4">
            <h2 class="text-base font-semibold text-gray-900 dark:text-white">
              Duration
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Define the validity window for this Jack access token.
            </p>
          </header>

          <hs-tabs v-model="form.durationMode" class="mb-4" :tabs="durationModeTabs" />

          <div v-if="form.durationMode === 'ttl'" class="space-y-3">
            <div class="flex flex-wrap gap-2">
              <hs-button
                v-for="option in ttlOptions"
                :key="option"
                size="sm"
                :variant="Number(form.ttlMinutes) === Number(option) ? 'primary' : 'secondary'"
                @click="form.ttlMinutes = option"
              >
                {{
                  option < 60
                    ? option + " min"
                    : option === 60
                    ? "1 hour"
                    : `${Math.round(option / 60)} hrs`
                }}
              </hs-button>
            </div>
            <div
              class="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-slate-900"
            >
              <vue-feather type="clock" class="h-4 w-4 text-gray-500" />
              <span>Expires {{ ttlPreview }}</span>
            </div>
          </div>

          <div v-else class="space-y-3">
            <hs-date-input
              v-model="expiresAtInputModel"
              type="datetime-local"
              label="Expires At"
              placeholder="Select expiry date & time"
              :min="currentDateTimeInput"
              class="w-full"
            />
            <p v-if="errors.duration" class="text-xs text-red-500">
              {{ errors.duration }}
            </p>
          </div>
        </section>
      </div>

      <aside class="space-y-4">
        <div
          class="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-100 p-5 shadow-lg dark:border-blue-800 dark:from-slate-900 dark:to-slate-800"
        >
          <div class="mb-4 flex items-center justify-between">
            <div>
              <h3
                class="text-sm font-semibold text-blue-900 dark:text-blue-100"
              >
                Access preview
              </h3>
              <p class="text-xs text-blue-800/80 dark:text-blue-200/90">
                Review before dispatching the grant.
              </p>
            </div>
            <vue-feather
              type="shield"
              class="h-6 w-6 text-blue-700 dark:text-blue-200"
            />
          </div>

          <dl class="space-y-3 text-sm text-blue-900 dark:text-blue-100">
            <div>
              <dt
                class="text-xs uppercase tracking-wide text-blue-800/80 dark:text-blue-200/80"
              >
                Store
              </dt>
              <dd class="font-semibold">{{ previewStoreLabel }}</dd>
            </div>
            <div>
              <dt
                class="text-xs uppercase tracking-wide text-blue-800/80 dark:text-blue-200/80"
              >
                User
              </dt>
              <dd class="font-semibold">{{ previewUserLabel }}</dd>
              <p
                v-if="selectedMember && selectedMember.user?.phone_number"
                class="text-xs text-blue-800/70 dark:text-blue-200/70"
              >
                {{ selectedMember.user.phone_number }}
              </p>
            </div>
            <div>
              <dt
                class="text-xs uppercase tracking-wide text-blue-800/80 dark:text-blue-200/80"
              >
                Expires
              </dt>
              <dd class="font-semibold">{{ expiryPreviewLabel }}</dd>
            </div>
          </dl>

          <div class="mt-4">
            <h4
              class="text-xs font-semibold uppercase tracking-wide text-blue-800/80 dark:text-blue-200/80"
            >
              Permissions
            </h4>
            <div
              v-if="!selectedPermissionMeta.length"
              class="mt-2 rounded-lg bg-white/40 p-3 text-xs text-blue-800/70 dark:bg-slate-900/50 dark:text-blue-200/70"
            >
              No permissions chosen yet.
            </div>
            <div v-else class="mt-2 flex flex-wrap gap-2">
              <span
                v-for="option in selectedPermissionMeta"
                :key="option.value"
                class="inline-flex items-center gap-1 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-blue-800 shadow-sm backdrop-blur dark:bg-slate-900/70 dark:text-blue-200"
              >
                <vue-feather
                  :type="option.readOnly ? 'eye' : 'unlock'"
                  class="h-3.5 w-3.5"
                />
                {{ option.label }}
              </span>
            </div>
          </div>

          <div
            class="mt-4 rounded-lg border border-blue-200/60 bg-white/50 p-3 text-xs text-blue-800/80 shadow-sm backdrop-blur dark:border-blue-800/80 dark:bg-slate-900/70 dark:text-blue-200/80"
          >
            <p>
              Grants are logged instantly. Revoking them early will block
              subsequent Jack activity and is strongly recommended once
              troubleshooting is complete.
            </p>
          </div>
        </div>

        <div class="jack-access-modal__dispatch-note">
          <h4>Dispatch</h4>
          <p>The access link becomes active immediately after confirmation.</p>
        </div>
      </aside>
    </div>
    <template #footer>
      <hs-button variant="secondary" @click="handleClose">Cancel</hs-button>
      <hs-button icon="key" :loading="creating" @click="handleSubmit">Create access</hs-button>
    </template>
  </hs-modal>
</template>

<script>
import { addMinutes, format, isValid } from "date-fns";
import http from "@/helper/http";
import api from "@/helper/api";
import AsyncStoreSelect from "@/components/jack/AsyncStoreSelect.vue";
import {
  HsBadge,
  HsButton,
  HsCheckbox,
  HsDateInput,
  HsInput,
  HsModal,
  HsSelect,
  HsTabs,
  HsTextarea,
} from "@/components/ui";

const WRITE_ACTION_PATTERN = /(WRITE|MANAGE|APPROVE|PROCESS|CHANGE)/i;

const normalizePermissionName = (name = "") => {
  const normalized = String(name || "").toUpperCase();
  if (normalized.startsWith("CAN_")) {
    return normalized;
  }
  if (normalized.includes("CAN_")) {
    return normalized.slice(normalized.indexOf("CAN_"));
  }
  return normalized;
};

const normalizePermissionSelection = (permissions = []) =>
  Array.from(new Set(permissions.map(normalizePermissionName).filter(Boolean)));

export default {
  name: "JackAccessModal",
  components: {
    AsyncStoreSelect,
    HsBadge,
    HsButton,
    HsCheckbox,
    HsDateInput,
    HsInput,
    HsModal,
    HsSelect,
    HsTabs,
    HsTextarea,
  },
  props: {
    selectedStoreId: {
      type: [Number, String],
      default: null,
    },
    selectedStore: {
      type: Object,
      default: null,
    },
    storeMembers: {
      type: Array,
      default: () => [],
    },
    storeMembersLoading: {
      type: Boolean,
      default: false,
    },
    permissionGroups: {
      type: Array,
      default: () => [],
    },
    permissionPresets: {
      type: Array,
      default: () => [],
    },
    creating: {
      type: Boolean,
      default: false,
    },
    isSuperAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedStoreSummary:
        this.selectedStore && typeof this.selectedStore === "object"
          ? { ...this.selectedStore }
          : null,
      form: {
        storeId:
          (this.selectedStore && this.selectedStore.id != null
            ? this.selectedStore.id
            : null) || this.selectedStoreId || null,
        userMode: this.storeMembers?.length ? "existing" : "manual",
        selectedMemberId: this.storeMembers?.[0]?.user?.id || null,
        manualPhoneNumber: "",
        permissionSelection: [],
        reason: "",
        durationMode: "ttl",
        ttlMinutes: 60,
        expiresAt: null,
        permissionSearch: "",
      },
      errors: {},
      phoneLookupLoading: false,
      phoneLookupResult: null,
      phoneLookupError: null,
      phoneLookupTimer: null,
      autoSelectStoreMember: true,
      durationModeTabs: [
        { id: "ttl", label: "Duration", icon: "clock" },
        { id: "expires_at", label: "Ends on date", icon: "calendar" },
      ],
    };
  },
  computed: {
    userModeTabs() {
      return [
        {
          id: "existing",
          label: "Store team",
          icon: "users",
          count: this.storeMembersOptions.length,
        },
        { id: "manual", label: "By phone", icon: "phone" },
      ];
    },
    selectedMemberModel: {
      get() {
        return String(this.form.selectedMemberId || "");
      },
      set(value) {
        this.handleMemberChange(value);
      },
    },
    storeMembersOptions() {
      return (this.storeMembers || []).map((member) => ({
        value: String(member?.user?.id || member?.user_id || ""),
        name: this.generateMemberLabel(member),
      }));
    },
    expiresAtModel: {
      get() {
        const value = this.form?.expiresAt;
        if (!value) {
          return null;
        }
        if (value instanceof Date) {
          return isValid(value) ? value : null;
        }
        const parsed = new Date(value);
        return isValid(parsed) ? parsed : null;
      },
      set(value) {
        if (!value) {
          this.form.expiresAt = null;
          return;
        }
        const nextValue = value instanceof Date ? value : new Date(value);
        this.form.expiresAt = isValid(nextValue) ? nextValue : null;
      },
    },
    expiresAtInputModel: {
      get() {
        const value = this.expiresAtModel;
        if (!value) return "";
        return format(value, "yyyy-MM-dd'T'HH:mm");
      },
      set(value) {
        this.expiresAtModel = value || null;
      },
    },
    permissionMap() {
      const map = new Map();
      this.permissionGroups.forEach((group) => {
        group.options.forEach((option) => {
          map.set(option.value, option);
        });
      });
      return map;
    },
    filteredPermissionPresets() {
      const query = (this.form.permissionSearch || "").trim().toLowerCase();
      if (!query) {
        return this.permissionPresets;
      }

      return this.permissionPresets.filter((preset) => {
        const labelMatch = (preset.label || "").toLowerCase().includes(query);
        const descriptionMatch = (preset.description || "")
          .toLowerCase()
          .includes(query);
        const permissionMatch = (preset.permissions || []).some(
          (permission) => {
            const normalized = String(permission || "");
            const option = this.permissionMap.get(normalized);
            if (option) {
              return (
                option.label.toLowerCase().includes(query) ||
                option.value.toLowerCase().includes(query)
              );
            }
            return normalized.toLowerCase().includes(query);
          }
        );

        return labelMatch || descriptionMatch || permissionMatch;
      });
    },
    selectedPresetKeys() {
      const selected = new Set(this.form.permissionSelection || []);
      return this.permissionPresets
        .filter((preset) => {
          const permissions = preset.permissions || [];
          if (!permissions.length) {
            return false;
          }
          return permissions.every((perm) => selected.has(perm));
        })
        .map((preset) => preset.key);
    },
    selectedPermissionMeta() {
      return this.form.permissionSelection
        .map((value) => this.permissionMap.get(value))
        .filter(Boolean);
    },
    selectedMember() {
      if (!this.form.selectedMemberId) {
        return null;
      }
      return (this.storeMembers || []).find(
        (member) =>
          String(member?.user?.id) === String(this.form.selectedMemberId)
      );
    },
    previewStoreLabel() {
      if (this.selectedStoreSummary && this.selectedStoreSummary.id != null) {
        return (
          this.selectedStoreSummary.name ||
          `Store #${this.selectedStoreSummary.id}`
        );
      }
      if (this.form.storeId) {
        return `Store #${this.form.storeId}`;
      }
      return "No store selected";
    },
    previewUserLabel() {
      if (this.form.userMode === "existing" && this.selectedMember) {
        if (this.selectedMember) {
          return this.generateMemberLabel(this.selectedMember);
        }
        return "Select a teammate";
      }
      if (this.form.manualPhoneNumber) {
        return this.form.manualPhoneNumber;
      }
      return "Enter a phone number";
    },
    expiryPreviewLabel() {
      if (this.form.durationMode === "ttl") {
        return this.ttlPreview;
      }
      if (this.form.expiresAt && isValid(this.form.expiresAt)) {
        return format(this.form.expiresAt, "MMM dd, yyyy HH:mm");
      }
      return "Select a date";
    },
    ttlPreview() {
      const minutes = Number(this.form.ttlMinutes || 0);
      if (!minutes || Number.isNaN(minutes)) {
        return "when TTL is defined";
      }
      const expiry = addMinutes(new Date(), minutes);
      return format(expiry, "MMM dd, yyyy HH:mm");
    },
    filteredPermissionGroups() {
      const query = (this.form.permissionSearch || "").trim().toLowerCase();
      if (!query) {
        return this.permissionGroups;
      }
      return this.permissionGroups
        .map((group) => {
          const options = group.options.filter(
            (option) =>
              option.label.toLowerCase().includes(query) ||
              option.value.toLowerCase().includes(query)
          );
          return {
            ...group,
            options,
          };
        })
        .filter((group) => group.options.length);
    },
    hasElevatedPermissions() {
      return this.form.permissionSelection.some((permission) =>
        WRITE_ACTION_PATTERN.test(permission)
      );
    },
    reasonCharacterCount() {
      return this.form.reason?.length || 0;
    },
    ttlOptions() {
      return [15, 30, 45, 60, 90, 120, 180, 240, 360, 720, 1440];
    },
    currentDateTimeInput() {
      return format(new Date(), "yyyy-MM-dd'T'HH:mm");
    },
    absoluteExpiryLabel() {
      if (this.form.expiresAt && isValid(this.form.expiresAt)) {
        return format(this.form.expiresAt, "EEE, MMM dd yyyy • HH:mm");
      }
      return "Select expiry date & time";
    },
  },
  watch: {
    selectedStore: {
      immediate: true,
      handler(store) {
        if (store && typeof store === "object" && store.id != null) {
          this.selectedStoreSummary = { ...store };
          this.form.storeId = store.id ?? null;
          this.autoSelectStoreMember = true;
        } else if (!this.selectedStoreId) {
          this.selectedStoreSummary = null;
          this.form.storeId = null;
          this.autoSelectStoreMember = false;
        }
      },
    },
    selectedStoreId: {
      immediate: true,
      handler(value) {
        if (value != null && value !== "" && value !== "ALL") {
          const numericValue = Number(value);
          this.form.storeId = numericValue;
          if (!this.selectedStoreSummary || this.selectedStoreSummary.id !== numericValue) {
            this.selectedStoreSummary = null;
          }
          this.autoSelectStoreMember = true;
        } else if (!this.selectedStoreSummary) {
          this.form.storeId = null;
          this.autoSelectStoreMember = false;
        }
      },
    },
    storeMembers: {
      immediate: true,
      handler(members) {
        if (Array.isArray(members) && members.length) {
          const firstMemberId = members[0]?.user?.id || members[0]?.user_id;
          if (this.autoSelectStoreMember) {
            this.form.userMode = "existing";
            this.form.selectedMemberId = firstMemberId ? Number(firstMemberId) : null;
            this.form.manualPhoneNumber = "";
            this.resetPhoneLookupState();
          } else if (this.form.userMode === "existing" && !this.form.selectedMemberId && firstMemberId) {
            this.form.selectedMemberId = Number(firstMemberId);
          }
        } else {
          if (this.autoSelectStoreMember) {
            this.form.userMode = "manual";
            this.form.manualPhoneNumber = "";
            this.resetPhoneLookupState();
          }
          this.form.selectedMemberId = null;
        }
      },
    },
    "form.manualPhoneNumber": {
      handler(value) {
        if (this.form.userMode !== "manual") {
          return;
        }
        if (this.phoneLookupTimer) {
          clearTimeout(this.phoneLookupTimer);
          this.phoneLookupTimer = null;
        }
        this.phoneLookupResult = null;
        this.phoneLookupError = null;
        const trimmed = (value || "").trim();
        if (!trimmed) {
          this.phoneLookupLoading = false;
          return;
        }
        if (trimmed.length>= 12) {
          this.phoneLookupLoading = true;
          this.phoneLookupTimer = setTimeout(() => {
            this.lookupPhoneNumber(trimmed);
          }, 400);
        } else {
          this.phoneLookupLoading = false;
        }
      },
    },
  },
  methods: {
    handleClose() {
      this.resetPhoneLookupState();
      this.$emit("close");
    },
    handleStoreSelected(store) {
      if (store && typeof store === "object" && store.id != null) {
        this.selectedStoreSummary = { ...store };
        this.form.storeId = Number(store.id);
      } else {
        this.selectedStoreSummary = null;
        this.form.storeId = null;
      }

      const hasStore = this.form.storeId != null;
      this.autoSelectStoreMember = hasStore;
      this.form.userMode = hasStore ? "existing" : "manual";
      this.form.selectedMemberId = null;
      this.form.manualPhoneNumber = "";
      this.resetPhoneLookupState();

      this.$emit("store-change", {
        storeId: this.form.storeId,
        store: this.selectedStoreSummary ? { ...this.selectedStoreSummary } : null,
      });
    },
    resetPhoneLookupState() {
      this.phoneLookupResult = null;
      this.phoneLookupError = null;
      this.phoneLookupLoading = false;
      if (this.phoneLookupTimer) {
        clearTimeout(this.phoneLookupTimer);
        this.phoneLookupTimer = null;
      }
    },
    handleMemberChange(value) {
      this.form.selectedMemberId = value ? Number(value) : null;
      if (this.form.userMode === "existing") {
        this.form.manualPhoneNumber = "";
        this.resetPhoneLookupState();
      }
    },
    isPermissionSelected(permission) {
      return this.form.permissionSelection.includes(permission);
    },
    setPermissionSelected(permission, selected) {
      if (selected) {
        if (!this.form.permissionSelection.includes(permission)) {
          this.form.permissionSelection = [...this.form.permissionSelection, permission];
        }
        return;
      }
      this.form.permissionSelection = this.form.permissionSelection.filter(
        (value) => value !== permission
      );
    },
    setUserMode(mode) {
      this.form.userMode = mode;
      this.autoSelectStoreMember = mode === "existing";
      if (mode === "existing" && this.storeMembersOptions.length) {
        const first = this.storeMembersOptions[0]?.value;
        this.form.selectedMemberId = first ? Number(first) : null;
        this.form.manualPhoneNumber = "";
      }
      if (mode === "manual") {
        this.form.selectedMemberId = null;
      }
      this.resetPhoneLookupState();
    },
    clearPermissions() {
      this.form.permissionSelection = [];
    },
    applyPreset(preset) {
      const unique = new Set(this.form.permissionSelection);
      preset.permissions.forEach((permission) => unique.add(permission));
      this.form.permissionSelection = Array.from(unique);
    },
    async lookupPhoneNumber(phoneNumber) {
      try {
        const response = await http.get(api.admin_jack_user_lookup, {
          params: { phone_number: phoneNumber },
        });
        this.phoneLookupResult = response?.data?.user || null;
        this.phoneLookupError = this.phoneLookupResult
          ? null
          : "User not found";
      } catch (error) {
        this.phoneLookupResult = null;
        this.phoneLookupError =
          error?.response?.data?.error?.message || "Unable to validate phone";
      } finally {
        this.phoneLookupLoading = false;
      }
    },
    togglePreset(preset) {
      const allSelected = (preset.permissions || []).every((permission) =>
        this.form.permissionSelection.includes(permission)
      );
      if (allSelected) {
        this.form.permissionSelection = this.form.permissionSelection.filter(
          (permission) => !preset.permissions.includes(permission)
        );
        return;
      }
      this.applyPreset(preset);
    },
    presetButtonClass(preset) {
      const isActive = this.selectedPresetKeys.includes(preset.key);
      return [
        "group flex-1 min-w-[180px] rounded-lg border px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
        isActive
          ? "border-blue-500 bg-blue-600 text-white shadow-lg shadow-blue-200/50 dark:border-blue-400 dark:bg-blue-500/40 dark:text-blue-100"
          : "border-blue-200 bg-blue-50 text-blue-700 hover:-translate-y-0.5 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
      ];
    },
    presetLabelClass(preset) {
      const isActive = this.selectedPresetKeys.includes(preset.key);
      return isActive
        ? "text-white dark:text-blue-50"
        : "text-blue-800 dark:text-blue-100";
    },
    presetDescriptionClass(preset) {
      const isActive = this.selectedPresetKeys.includes(preset.key);
      return isActive
        ? "text-blue-100/90"
        : "text-blue-600/80 dark:text-blue-200/90";
    },
    generateMemberLabel(member) {
      const name = member?.user?.names || member?.user?.username;
      const phone = member?.user?.phone_number;
      if (name && phone) {
        return `${name} (${phone})`;
      }
      if (name) {
        return name;
      }
      if (phone) {
        return phone;
      }
      return `User #${member?.user?.id || member?.user_id}`;
    },
    generatePermissionDescription(permission) {
      const normalized = String(permission || "");
      if (normalized.includes("PRODUCT")) {
        return "Access catalog products";
      }
      if (normalized.includes("SERVICE")) {
        return "Access catalog services";
      }
      if (normalized.includes("PURCHASE")) {
        return "Work with purchase orders and suppliers";
      }
      if (normalized.includes("EXPENSE")) {
        return "Review or post expenses";
      }
      if (normalized.includes("SALE")) {
        return "Inspect or manage sales carts";
      }
      if (normalized.includes("CUSTOMER")) {
        return "View or manage customer records";
      }
      if (normalized.includes("REPORT")) {
        return "Access business reports";
      }
      if (normalized.includes("SETTING")) {
        return "Adjust store settings";
      }
      if (normalized.includes("PROMOS")) {
        return "Work with promotional codes";
      }
      if (normalized.includes("DASHBOARD")) {
        return "Share store dashboards";
      }
      if (normalized.includes("RRA") || normalized.includes("EBM")) {
        return "Manage RRA/EBM compliance data";
      }
      return "Grant specific store capability";
    },
    validate() {
      const errors = {};
      if (!this.form.storeId) {
        errors.storeId = "Store is required";
      }

      let phoneNumber = null;
      if (this.form.userMode === "existing") {
        if (this.selectedMember) {
          phoneNumber = this.selectedMember?.user?.phone_number;
        }
        if (!phoneNumber) {
          errors.targetUser = "Select a teammate";
        }
      } else {
        phoneNumber = (this.form.manualPhoneNumber || "").trim();
        if (!phoneNumber) {
          errors.targetUser = "Provide a valid phone number";
        } else if (
          !this.phoneLookupResult ||
          this.phoneLookupResult?.phone_number !== phoneNumber
        ) {
          errors.targetUser =
            this.phoneLookupError ||
            "Validate the phone number before proceeding";
        }
      }

      if (!this.form.reason || !this.form.reason.trim()) {
        errors.reason = "Explain why this access is needed";
      }

      if (!this.form.permissionSelection.length) {
        errors.permissions = "Select at least one permission";
      }

      if (this.form.durationMode === "ttl") {
        const ttl = Number(this.form.ttlMinutes);
        if (!ttl || Number.isNaN(ttl)) {
          errors.duration = "Choose a TTL window";
        }
      } else if (!this.form.expiresAt || !isValid(this.form.expiresAt)) {
        errors.duration = "Pick a valid expiry date";
      }

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    handleSubmit() {
      if (!this.validate()) {
        return;
      }

      const payload = {
        store_id: Number(this.form.storeId),
        phone_number:
          this.form.userMode === "existing" && this.selectedMember
            ? this.selectedMember?.user?.phone_number
            : this.phoneLookupResult?.phone_number ||
              this.form.manualPhoneNumber.trim(),
        permissions: normalizePermissionSelection(this.form.permissionSelection),
        reason: this.form.reason.trim(),
      };

      if (this.form.durationMode === "ttl") {
        payload.ttl_minutes = Number(this.form.ttlMinutes);
      } else if (this.form.expiresAt && isValid(this.form.expiresAt)) {
        payload.expires_at = format(this.form.expiresAt, "dd/MM/yyyy HH:mm:ss");
      }

      this.$emit("submit", payload);
    },
  },
};
</script>

<style scoped>
.jack-access-modal {
  display: grid;
  gap: 1.25rem;
  grid-template-columns: minmax(0, 1.35fr) minmax(18rem, 0.85fr);
}

.jack-access-modal__dispatch-note {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 68%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 16px;
  padding: 1rem;
}

.jack-access-modal__dispatch-note h4 {
  color: var(--hs-text, #0f172a);
  font-size: 0.9rem;
  font-weight: 950;
  margin: 0;
}

.jack-access-modal__dispatch-note p {
  color: var(--hs-muted, #64748b);
  font-size: 0.8rem;
  font-weight: 800;
  line-height: 1.5;
  margin: 0.35rem 0 0;
}

.jack-access-modal__permission {
  align-items: flex-start;
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 94%, transparent);
  border: 1px solid transparent;
  border-radius: 14px;
  display: flex;
  gap: 0.8rem;
  justify-content: space-between;
  padding: 0.85rem;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.jack-access-modal__permission:hover {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 5%, var(--hs-surface, #ffffff));
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 20%, var(--hs-border, rgba(148, 163, 184, 0.22)));
}

.jack-access-modal__permission :deep(.hs-checkbox) {
  flex: 1;
  min-width: 0;
}

summary::-webkit-details-marker {
  display: none;
}

summary {
  list-style: none;
}

@media (max-width: 1024px) {
  .jack-access-modal {
    grid-template-columns: 1fr;
  }
}
</style>
